import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import { pathgen } from 'object-path-generator';
import { useLocation } from 'react-router';
import { Spinner, Typography, useOverlayContext } from 'seagull';
import { redirect, routes } from '~/constants';
import { useIoContext } from '~/contexts';
import { useNativeOAuthSignIn } from '~/hooks/useNativeOAuthSignIn';
import { LoginMethod, useSendLoginGtmEvents } from '~/hooks/useSendLoginGtmEvents';
import { useUtmsParams } from '~/hooks/useUtmsParams';
import { useLocaleKeys } from '~/i18n';
import { handleRequest } from '~/infra/utils';
import XeroLogo from '~/integrations/assets/XeroLogo';
import { useStoreActions } from '~/store/store-context';
import styles from './XeroSignInButton.module.scss';
export const XeroSignInButtonTestIds = pathgen('XeroSignInButtonTestIds');
export default function XeroSignInButton(props) {
    const { mode, ensureEmail, width } = props;
    const getUtms = useUtmsParams();
    const { api } = useStoreActions();
    const { t: commonTexts } = useLocaleKeys('common');
    const { t: xeroTexts } = useLocaleKeys('pages.auth.xero');
    const { pathname, search } = useLocation();
    const { showErrorToast } = useOverlayContext();
    const { isNative, dispatchOAuth } = useNativeOAuthSignIn('xero');
    const { browser: { getOrigin } } = useIoContext();
    const [loading, setLoading] = useState(false);
    const { sendGtmLoginSuccessEvent, sendGtmLoginFailEvent } = useSendLoginGtmEvents();
    const returnTo = useMemo(()=>redirect.getReturnToFromSearch(search), [
        search
    ]);
    const iconSize = 24;
    async function handleClick() {
        setLoading(true);
        const utms = getUtms();
        const webOrigin = isNative ? __BASE_URL__ : getOrigin();
        const redirectUrl = returnTo ? "".concat(webOrigin).concat(redirect.getPathAndSearch({
            returnTo
        })) : "".concat(webOrigin).concat(routes.home);
        const request = mode === 'signUp' ? api.auth.xeroSignUp({
            redirectUrl,
            utms,
            native: isNative
        }) : api.auth.xeroSignIn({
            redirectUrl,
            utms,
            ensureEmail: ensureEmail ? {
                email: ensureEmail,
                errorUrl: "".concat(webOrigin).concat(pathname).concat(routes.loginError)
            } : undefined,
            native: isNative
        });
        const { success, data } = await handleRequest(request);
        const showXeroError = ()=>showErrorToast(commonTexts.toasts.failedToContinueWithXero(), XeroSignInButtonTestIds.ErrorToast());
        if (!success || !data) {
            setLoading(false);
            if (mode === 'signIn') {
                sendGtmLoginFailEvent(LoginMethod.Xero);
            }
            showXeroError();
            return;
        }
        if (mode === 'signIn') {
            await sendGtmLoginSuccessEvent(LoginMethod.Xero);
        }
        try {
            await dispatchOAuth(data);
            if (isNative) setLoading(false);
        } catch (unused) {
            setLoading(false);
            showXeroError();
        }
    }
    return /*#__PURE__*/ _jsx("div", {
        className: styles.root,
        children: /*#__PURE__*/ _jsxs("button", {
            className: styles.button,
            "data-testid": XeroSignInButtonTestIds.Button(),
            disabled: loading,
            onClick: handleClick,
            style: width != null ? {
                width
            } : undefined,
            children: [
                loading ? /*#__PURE__*/ _jsx(Spinner, {
                    size: iconSize,
                    testId: XeroSignInButtonTestIds.Loading()
                }) : /*#__PURE__*/ _jsx(XeroLogo, {
                    height: iconSize
                }),
                /*#__PURE__*/ _jsx(Typography, {
                    skin: loading ? 'disabled' : 'standard',
                    children: xeroTexts.continueWithXero()
                })
            ]
        })
    });
}
